import React, { useEffect } from 'react';

import AddIcon from '@mui/icons-material/Add';
import { Button, Box } from '@sunwisesoftware/sunwise-ui';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import PreviewLocation from 'common/components/PreviewLocation';
import { DEFAULT_MAP_PREVIEW_ZOOM } from 'common/constants';

import * as actions from './actions';
import FormLocation from './components/FormLocation';
import { getFormName } from './helpers';
import * as selectors from './selectors';

const Container = ({
    addressPlaceholder,
    center,
    editMode,
    geocode,
    geocodeResult,
    handleSubmit,
    initPosition = {},
    initialValues,
    initializeForm,
    isForLayout,
    name,
    resetForm,
    setEditMode,
}) => {
    const { t } = useTranslation();
    useEffect(() => {
        if (editMode) {
            const values = {
                city: get(initPosition, 'position.city', ''),
                cp: get(initPosition, 'position.zip_code', ''),
                description: initPosition.description,
                hasPostalAddress: initPosition.position
                    ? initPosition.position.additional_information
                        ? true
                        : false
                    : false,
                id: initPosition.id ? initPosition.id : 0,
                political_division1: get(
                    initPosition,
                    'position.political_division1.id',
                    null,
                ),
                position: {
                    latitude: get(initPosition, 'position.latitude', null),
                    longitude: get(initPosition, 'position.longitude', null),
                },
                postalAddress: get(
                    initPosition,
                    'position.additional_information',
                    '',
                ),
                title: initPosition.title,
            };
            initializeForm(values);
        } else {
            resetForm();
        }
        return () => {
            resetForm();
        };
    }, [editMode]);

    if (editMode)
        return (
            <FormLocation
                addressPlaceholder={addressPlaceholder}
                center={center}
                errors={[]}
                geocodeResult={geocodeResult}
                handleClickCancel={() => setEditMode(false)}
                handleGeocode={geocode}
                initialValues={initialValues}
                isForLayout={isForLayout}
                isSaving={false}
                name={name}
                onSubmit={handleSubmit}
                zoom={DEFAULT_MAP_PREVIEW_ZOOM}
            />
        );

    if (get(initPosition, 'position', null) === null)
        return (
            <Box p={2}>
                <Button
                    endIcon={<AddIcon />}
                    fullWidth
                    onClick={() => setEditMode(true)}
                    variant="outlined"
                >
                    {t('Add location')}
                </Button>
            </Box>
        );

    return <PreviewLocation position={initPosition} />;
};

const mapStateToProps = createStructuredSelector({
    geocodeResult: selectors.getFetchGeocodeData,
    initialValues: selectors.getInitialValues,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
    initializeForm: (values) =>
        dispatch(actions.initializeForm(values, getFormName(ownProps.name))),
    geocode: (str) =>
        dispatch(actions.fetchGeocodeResult(str, getFormName(ownProps.name))),
    resetForm: () => dispatch(actions.resetForm(getFormName(ownProps.name))),
});

Container.propTypes = {
    addressPlaceholder: PropTypes.string,
    center: PropTypes.object,
    editMode: PropTypes.bool,
    geocode: PropTypes.func,
    geocodeResult: PropTypes.array,
    handleSubmit: PropTypes.func,
    initializeForm: PropTypes.func,
    initialValues: PropTypes.object,
    initPosition: PropTypes.object,
    isForLayout: PropTypes.bool,
    name: PropTypes.string,
    resetForm: PropTypes.func,
    setEditMode: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(Container);
